import CloseIcon from '@mui/icons-material/Close';
import Autocomplete, { AutocompleteCloseReason } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';
import React, { FC, useCallback, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';

import Plus from '@/assets/svgs/dropdown/add-sign.icon.svg?react';
import ArrowDown from '@/assets/svgs/dropdown/down-arrow.icon.svg?react';
import SearchIcon from '@/assets/svgs/dropdown/search.icon.svg?react';
import Button, { BtnVariant } from '@/shared/components/core-ui/button/button.component';
import StyledFormControl from '@/shared/components/core-ui/form/styled-components/styled-form-control';
import { StyledIconButton } from '@/shared/components/group-ui/form-controls/select/styled-select-componenets/styled-select-icon-btn';
import {
  PopperComponent,
  StyledPopper
} from '@/shared/components/group-ui/form-controls/select/styled-select-componenets/styled-select-popper';
import SearchTextField from '@/shared/components/group-ui/form-controls/select/styled-select-componenets/styled-select-search-text';
import { ChangeEvent } from '@/shared/types/shared-types/change-event.type';
import { SelectOptionsProps } from '@/shared/types/shared-types/select.type';

interface SelectWithSearchProps {
  data: {
    name: string;
    label: string;
    placeholder: string;
    searchTextPlaceholder: string;
    selectOptions: SelectOptionsProps[];
    value: SelectOptionsProps | null;
    error: any;
    dataLoading?: boolean;
    searchLoading: boolean;
    noResultsFoundText: string;
  };
  config?: {
    addBtnText?: string;
    isDisabled?: boolean;
    isSearchDisabled?: boolean;
    isVerified?: boolean;
    required?: boolean;
  };
  eventHandlers: {
    handleInputChange: (e: ChangeEvent<SelectOptionsProps | null>) => void;
    setSearchLoading: (loading: boolean) => void;
    setSearchQuery: (searchQuery: string) => void;
    handleAddBtnClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  };
}

const FormSelectWithSearch: FC<SelectWithSearchProps> = ({ data, config = {}, eventHandlers }) => {
  const {
    name,
    label,
    placeholder,
    searchTextPlaceholder,
    value,
    error = null,
    dataLoading = false,
    selectOptions,
    searchLoading,
    noResultsFoundText
  } = data;

  const {
    addBtnText,
    isDisabled = false,
    isSearchDisabled = false,
    isVerified,
    required = false
  } = config;

  const { handleInputChange, setSearchLoading, setSearchQuery, handleAddBtnClick } = eventHandlers;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [searchValue, setSearchValue] = useState<string>('');

  const open = Boolean(anchorEl);
  const id = open ? name : undefined;

  const theme = useTheme();

  // Use the option from the list that matches current value so Autocomplete never receives a "free" value
  // (which can cause it to clear the selection). Display and X mark still use parent `value`.
  const autocompleteValue =
    selectOptions.find((opt) => (opt?.id ?? '') === (value?.id ?? '')) ?? null;

  const getInitials = (name: string | undefined | null): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
    return (first + second).toUpperCase() || '?';
  };

  const splitLegacySubLabel = (
    option: SelectOptionsProps
  ): { phone?: string; tags?: string[]; roles?: string[] } => {
    const parts = option.subLabel ?? [];
    if (!parts.length) return {};
    const phoneLike = parts.find((p) => /^\+?\d{6,}$/.test(String(p).replace(/\s+/g, '')));
    const rest = parts.filter((p) => p !== phoneLike);
    // Heuristic: treat "Referral #..." and other short descriptors as tags, remaining as roles
    const tags = rest.filter((p) => String(p).toLowerCase().includes('referral #'));
    const roles = rest.filter((p) => !String(p).toLowerCase().includes('referral #'));
    return {
      ...(phoneLike ? { phone: String(phoneLike) } : {}),
      ...(tags.length ? { tags } : {}),
      ...(roles.length ? { roles } : {})
    };
  };

  const handleDropDownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropDownClose = () => {
    setAnchorEl(null);
    setSearchValue('');
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setSearchLoading(true);
    debouncedSearch(e.target.value);
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 1000),
    []
  );

  const getFieldIcon = () => {
    if (dataLoading) return <CircularProgress size={20} style={{ marginRight: '8px' }} />;
    // Show clear (X) when there is a selection: value has a non-empty id
    if (value != null && String(value?.id ?? '').trim() !== '' && !isDisabled)
      return (
        <>
          {isVerified && <FaCheckCircle size={20} style={{ color: 'green', marginRight: '8px' }} />}

          <StyledIconButton
            onClick={(event) => {
              event.stopPropagation();
              handleInputChange({ target: { name, value: { id: '', label: '' } } });
            }}
          >
            <CloseIcon fontSize="small" style={{ color: isVerified === false ? 'red' : '' }} />
          </StyledIconButton>
        </>
      );

    return (
      <>
        <ArrowDown />
      </>
    );
  };

  return (
    <React.Fragment>
      <StyledFormControl>
        <FormLabel required={required}>{label}</FormLabel>
        <TextField
          name={name}
          // Keep TextField controlled; show label or id so selection is always visible
          value={value != null ? (value?.label ?? value?.id ?? '') : ''}
          placeholder={placeholder}
          onClick={handleDropDownOpen}
          disabled={isDisabled}
          label=""
          fullWidth
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Avatar
                  src={value?.imageUrl || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    ml: 1,
                    bgcolor: theme.palette.grey[200],
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}
                >
                  {getInitials(value?.label)}
                </Avatar>
              </InputAdornment>
            ),
            endAdornment: getFieldIcon()
          }}
          sx={{
            '& .MuiInputBase-input': {
              color: theme.palette.grey[400],
              padding: '17px 0px !important'
            },
            '& .MuiOutlinedInput-root': { border: error && `1px solid ${theme.palette.error.main}` }
          }}
        />
        <FormHelperText error>{error ? error : ' '}</FormHelperText>
        <StyledPopper
          id={id}
          open={open && !isDisabled}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{
            width: anchorEl ? anchorEl.clientWidth : undefined
          }}
        >
          <ClickAwayListener onClickAway={handleDropDownClose}>
            <Autocomplete
              open
              value={autocompleteValue}
              options={selectOptions}
              getOptionLabel={(option) => (option.label ? option.label : '')}
              isOptionEqualToValue={(option, val) => (option?.id ?? '') === (val?.id ?? '')}
              filterOptions={(x) => x}
              noOptionsText={noResultsFoundText}
              loading={searchLoading}
              PopperComponent={PopperComponent}
              onClose={(event: React.ChangeEvent<object>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleDropDownClose();
                }
              }}
              onHighlightChange={(event, newValue, reason) => {
                if (reason === 'touch') {
                  handleInputChange({
                    target: {
                      name,
                      value: {
                        id: newValue?.id ?? '',
                        label: newValue?.label ?? '',
                        subLabel: newValue?.subLabel ?? [],
                        ...(newValue?.imageUrl && { imageUrl: newValue?.imageUrl ?? '' })
                      }
                    }
                  });
                }
              }}
              onChange={(event, newValue, reason) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return;
                }
                handleInputChange({
                  target: {
                    name,
                    value: {
                      id: newValue?.id ?? '',
                      label: newValue?.label ?? '',
                      subLabel: newValue?.subLabel ?? [],
                      ...(newValue?.imageUrl && { imageUrl: newValue?.imageUrl ?? '' })
                    }
                  }
                });
                handleDropDownClose();
              }}
              renderOption={(props, option) => {
                const meta = option.meta ?? splitLegacySubLabel(option);
                const secondaryLeft = [meta.phone, ...(meta.tags ?? [])]
                  .filter(Boolean)
                  .join(' • ');
                const roles = meta.roles ?? [];
                return (
                  <li
                    style={{
                      ...(value && option.id === value.id
                        ? { backgroundColor: theme.palette.action.hover }
                        : {}),
                      paddingTop: 12,
                      paddingBottom: 12
                    }}
                    {...props}
                    key={option.id}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={option?.imageUrl || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: theme.palette.grey[200],
                            color: theme.palette.text.secondary,
                            fontSize: '0.9rem',
                            fontWeight: 700
                          }}
                        >
                          {getInitials(option?.label)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {option.label}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              mt: 0.25
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {secondaryLeft}
                            </Typography>
                            {roles.length > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  flexWrap: 'wrap',
                                  justifyContent: 'flex-end'
                                }}
                              >
                                {roles.slice(0, 3).map((r) => (
                                  <Chip
                                    key={r}
                                    label={r}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      borderColor: theme.palette.grey[300],
                                      color: theme.palette.text.secondary
                                    }}
                                  />
                                ))}
                                {roles.length > 3 && (
                                  <Chip
                                    label={`+${roles.length - 3}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      borderColor: theme.palette.grey[300],
                                      color: theme.palette.text.secondary
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => {
                return (
                  <SearchTextField
                    ref={params.InputProps.ref}
                    inputProps={{
                      ...params.inputProps,
                      value: searchValue,
                      disabled: isSearchDisabled
                    }}
                    placeholder={searchTextPlaceholder}
                    variant="standard"
                    autoFocus
                    onChange={onSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchLoading ? (
                        <CircularProgress size={20} style={{ marginRight: '8px' }} />
                      ) : null,
                      disableUnderline: true
                    }}
                  />
                );
              }}
            />
          </ClickAwayListener>
          {addBtnText && (
            <Button
              config={{ variant: BtnVariant.TEXT, startIcon: <Plus /> }}
              eventHandlers={{ handleClick: handleAddBtnClick ? handleAddBtnClick : () => null }}
              children={addBtnText}
              styles={{ width: '100%', justifyContent: 'left' }}
            />
          )}
        </StyledPopper>
      </StyledFormControl>
    </React.Fragment>
  );
};

export default FormSelectWithSearch;
